import Grid from "@mui/material/Grid";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import Createable from "containers/Select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getWeeksArray } from "utils/common";
import { chartTypes } from "utils/common";
import {
  formatLocalDateToPerDayYmd,
  getPerDayRangeMaxEndYmd,
  perDayYmdToPickerSelected,
  validatePerDayDateRange,
} from "utils/perDayDietChart";

/** Keeps picker popover above stacked form controls. */
const datePickerPopperZ = 1400;

function datePickerFieldSx(hasError) {
  return {
    width: "100%",
    minWidth: 0,
    "& .react-datepicker-wrapper": { display: "block", width: "100%" },
    "& .react-datepicker__input-container input": {
      width: "100%",
      maxWidth: "100%",
      boxSizing: "border-box",
      padding: "8px 12px",
      borderRadius: "0.5rem",
      fontSize: 14,
      border: "1px solid",
      borderColor: hasError ? "error.main" : "grey.300",
    },
  };
}

const selectFullWidthStyle = {
  borderRadius: "0.5rem",
  fontSize: 14,
  width: "100%",
  minWidth: 0,
};
const selectInputFullWidth = {
  width: "100%",
  minWidth: 0,
  height: "1.800rem",
  borderRadius: "0.5rem",
};

export default function WeeklyPerDayMetaForm({
  formData,
  setFormData,
  errorMessage,
  setErrorMessage,
  clients,
  mode,
  client,
}) {
  const weekOptions = getWeeksArray();

  const onFromToChange = (field, value) => {
    let next = { ...formData, [field]: value };
    if (field === "from" && value) {
      const maxTo = getPerDayRangeMaxEndYmd(value);
      if (next.to) {
        if (next.to < value) next = { ...next, to: "" };
        else if (maxTo && next.to > maxTo) next = { ...next, to: maxTo };
      }
    }
    if (next.from && next.to) {
      const v = validatePerDayDateRange(next.from, next.to);
      if (!v.ok) {
        setErrorMessage((e) => ({ ...e, from: true, to: true }));
      } else {
        setErrorMessage((e) => ({ ...e, from: false, to: false }));
      }
    }
    setFormData(next);
  };

  const endDateMin = formData.from || undefined;
  const endDateMax = formData.from
    ? getPerDayRangeMaxEndYmd(formData.from)
    : undefined;

  const endMinDate = endDateMin ? perDayYmdToPickerSelected(endDateMin) : undefined;
  const endMaxDate =
    endDateMin && endDateMax ? perDayYmdToPickerSelected(endDateMax) : undefined;

  const onStartPick = (pickedDate) => {
    const ymd = formatLocalDateToPerDayYmd(pickedDate);
    onFromToChange("from", ymd);
  };

  const onEndPick = (pickedDate) => {
    const ymd = formatLocalDateToPerDayYmd(pickedDate);
    onFromToChange("to", ymd);
  };

  return (
    <Grid
      container
      spacing={2}
      mb={2}
      rowSpacing={2.5}
      columnSpacing={{ xs: 2, md: 2.5 }}
      sx={{ "& > .MuiGrid-item": { minWidth: 0 } }}
    >
      <Grid item xs={12} md={4}>
        <SuiTypography mb={0.75} variant="h6" fontWeight={600}>
          Client*
        </SuiTypography>
        <Createable
          options={
            mode === "edit" || mode === "view"
              ? [
                  {
                    label: client?.name + ` (+91${client?.phoneNumber})`,
                    value: client?.id,
                    name: client?.name,
                    key: client?.id,
                  },
                ]
              : clients ?? []
          }
          disabled={mode === "view"}
          value={formData?.clientName}
          style={selectFullWidthStyle}
          inputStyle={selectInputFullWidth}
          placeholder="Choose client"
          onChange={(e) => {
            setFormData({ ...formData, clientName: e });
            setErrorMessage((err) => ({ ...err, clientName: false }));
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <SuiTypography mb={0.75} variant="h6" fontWeight={600}>
          Start date*
        </SuiTypography>
        <SuiBox sx={datePickerFieldSx(Boolean(errorMessage?.from))}>
          <DatePicker
            isClearable
            closeOnScroll
            disabled={mode === "view"}
            autoComplete="off"
            selected={perDayYmdToPickerSelected(formData?.from)}
            onChange={onStartPick}
            placeholderText="Start date"
            dateFormat="dd/MM/yyyy"
            popperClassName="weekly-per-day-datepicker-popper"
            popperProps={{ style: { zIndex: datePickerPopperZ } }}
          />
        </SuiBox>
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <SuiTypography mb={0.75} variant="h6" fontWeight={600}>
          End date*
        </SuiTypography>
        <SuiBox sx={datePickerFieldSx(Boolean(errorMessage?.to))}>
          <DatePicker
            isClearable
            closeOnScroll
            disabled={mode === "view"}
            autoComplete="off"
            selected={perDayYmdToPickerSelected(formData?.to)}
            onChange={onEndPick}
            minDate={endMinDate}
            maxDate={endMaxDate}
            placeholderText="End date"
            dateFormat="dd/MM/yyyy"
            popperClassName="weekly-per-day-datepicker-popper"
            popperProps={{ style: { zIndex: datePickerPopperZ } }}
          />
        </SuiBox>
      </Grid>
      <Grid item xs={12} sm={6} md={6}>
        <SuiTypography mb={0.75} variant="h6" fontWeight={600}>
          Week plan*
        </SuiTypography>
        <Createable
          options={weekOptions}
          value={formData?.weekPlan}
          disabled={mode === "view"}
          onChange={(e) => setFormData({ ...formData, weekPlan: e })}
          style={selectFullWidthStyle}
          inputStyle={selectInputFullWidth}
          placeholder="Week plan"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={6}>
        <SuiTypography mb={0.75} variant="h6" fontWeight={600}>
          Chart type*
        </SuiTypography>
        <Createable
          options={chartTypes}
          value={formData?.chartType}
          disabled={mode === "view"}
          onChange={(e) => setFormData({ ...formData, chartType: e })}
          style={selectFullWidthStyle}
          inputStyle={selectInputFullWidth}
          placeholder="Chart type"
        />
      </Grid>
      <Grid item xs={12}>
        <SuiTypography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          Range is limited to 7 days (inclusive). PDF shows one dated section per day.
        </SuiTypography>
      </Grid>
    </Grid>
  );
}
